// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { parseLocalDate } from '@/utils/formatters';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Calendar, Loader2, Edit2, Trash2, MoreVertical } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import EventSelectionModal from '@/components/vendor-video/EventSelectionModal';

interface VendorEventAvailability {
  id: string;
  vendor_id: string;
  event_id?: string;
  event_name: string;
  event_date: string;
  is_available: boolean;
  created_at: string;
}

interface VideoVendorProfile {
  id: string;
  user_id: string;
  business_name?: string;
}

const VendorEvents: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<VendorEventAvailability[]>([]);
  const [vendorProfile, setVendorProfile] = useState<VideoVendorProfile | null>(null);
  const [showEventModal, setShowEventModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, [user?.id]);

  const fetchData = async () => {
    if (!user?.id) return;

    try {
      // Get vendor profile
      const { data: profileData, error: profileError } = await supabase
        .from('video_vendor_profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (profileError) throw profileError;
      setVendorProfile(profileData);

      // Get events if we have a vendor profile
      if (profileData?.id) {
        const { data: eventsData, error: eventsError } = await supabase
          .from('vendor_event_availability')
          .select('*')
          .eq('vendor_id', profileData.id)
          .order('event_date', { ascending: true });

        if (eventsError) throw eventsError;
        setEvents(eventsData || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return parseLocalDate(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleToggleAvailability = async (event: VendorEventAvailability) => {
    try {
      const { error } = await supabase
        .from('vendor_event_availability')
        .update({ is_available: !event.is_available })
        .eq('id', event.id);

      if (error) throw error;

      toast.success(`Availability updated to ${!event.is_available ? 'Available' : 'Unavailable'}`);
      fetchData();
    } catch (error) {
      console.error('Error updating availability:', error);
      toast.error('Failed to update availability');
    }
  };

  const handleDeleteEvent = async (event: VendorEventAvailability) => {
    try {
      const { error } = await supabase
        .from('vendor_event_availability')
        .delete()
        .eq('id', event.id);

      if (error) throw error;

      toast.success('Event removed from your availability');
      fetchData();
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Failed to remove event');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center gap-2">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span>Loading events...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Event Availability</h1>
          <p className="text-muted-foreground">Manage your event calendar and availability</p>
        </div>
        <Button
          onClick={() => setShowEventModal(true)}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Event
        </Button>
      </div>

      <div className="grid gap-6">
        {events.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No events scheduled</h3>
              <p className="text-muted-foreground text-center mb-4">
                Select events where you want to offer your video services
              </p>
              <Button onClick={() => setShowEventModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Select Events
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {events.map((event) => (
              <Card key={event.id}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Calendar className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <h3 className="font-medium">{event.event_name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(event.event_date)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={event.is_available ? "default" : "secondary"}>
                        {event.is_available ? 'Available' : 'Unavailable'}
                      </Badge>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleToggleAvailability(event)}
                            className="flex items-center gap-2"
                          >
                            <Edit2 className="h-4 w-4" />
                            {event.is_available ? 'Mark Unavailable' : 'Mark Available'}
                          </DropdownMenuItem>
                          
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <DropdownMenuItem
                                onSelect={(e) => e.preventDefault()}
                                className="flex items-center gap-2 text-destructive focus:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                                Remove Event
                              </DropdownMenuItem>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Remove Event</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to remove "{event.event_name}" from your availability? 
                                  This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteEvent(event)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Remove
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {vendorProfile && (
        <EventSelectionModal
          open={showEventModal}
          onOpenChange={setShowEventModal}
          vendorId={vendorProfile.id}
          onEventUpdate={fetchData}
        />
      )}
    </div>
  );
};

export default VendorEvents;